import { Input, Tooltip, cn } from '@heroui/react';
import CodeMirror from '@uiw/react-codemirror';
import { Search } from 'iconoir-react';
import { useCallback, useMemo, type FocusEvent } from 'react';

import { createExtensions } from './extensions/createExtensions';
import { useJsonPath } from './hooks/useJsonPath';
import { useJsonValidation } from './hooks/useJsonValidation';
import { createThemeForLanguage } from './themes';
import type { CodeProps } from './types';

import { formatJsonString } from '~/common/helpers/formatJsonString';
import i18n from '~/common/helpers/i18n';
import { isParseableJson } from '~/common/helpers/isParseableJson';

export function Code({
  readOnly = false,
  value = '{}',
  onChange,
  onError,
  height = '400px',
  width = '100%',
  classNames,
  description,
  enableJsonPathSearch,
  disableJsonValidation = false,
  language = 'json',
  placeholder,
  lineWrapping = false,
  formatOnBlur
}: CodeProps) {
  const shouldFormatOnBlur = formatOnBlur ?? language === 'json';

  const { error, handleChange, cancelPendingValidation, clearError } =
    useJsonValidation(language, disableJsonValidation, onChange, onError);

  const { jsonPathResult, handleInputChange } = useJsonPath(value);

  const displayValue = language === 'json' ? jsonPathResult || value : value;

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (
        event.relatedTarget instanceof Node &&
        event.currentTarget.contains(event.relatedTarget)
      ) {
        return;
      }

      if (!shouldFormatOnBlur || language !== 'json' || readOnly) {
        return;
      }

      if (!isParseableJson(value)) {
        return;
      }

      cancelPendingValidation();
      clearError();
      onChange?.(formatJsonString(value), null);
    },
    [
      shouldFormatOnBlur,
      language,
      readOnly,
      value,
      onChange,
      cancelPendingValidation,
      clearError
    ]
  );

  const wrapperClassNames = cn(
    'relative overflow-hidden flex flex-col',
    classNames?.wrapper
  );
  const codeMirrorClassNames = cn('overflow-auto', classNames?.codeMirror);

  const hasError = Boolean(error);

  const extensions = useMemo(
    () =>
      createExtensions(
        language,
        enableJsonPathSearch || false,
        hasError,
        readOnly,
        disableJsonValidation,
        lineWrapping
      ),
    [
      language,
      enableJsonPathSearch,
      hasError,
      readOnly,
      disableJsonValidation,
      lineWrapping
    ]
  );

  return (
    <div className={wrapperClassNames} onBlur={handleBlur}>
      <CodeMirror
        className={codeMirrorClassNames}
        readOnly={readOnly}
        value={displayValue}
        onChange={handleChange}
        theme={createThemeForLanguage(readOnly, language)}
        width={width}
        extensions={extensions}
        basicSetup={{
          lintKeymap: false,
          searchKeymap: false,
          lineNumbers: language !== 'markdown',
          foldGutter: language === 'markdown'
        }}
        height={height}
        style={{
          height
        }}
        placeholder={placeholder}
      />

      {error && (
        <div
          className={`absolute ${enableJsonPathSearch || description ? '' : 'rounded-b-md'} bottom-0 left-0 w-full border-t border-red-200 bg-red-50 p-2 text-xs text-red-600`}
        >
          <Tooltip content={error} placement='top' showArrow>
            <div className='truncate cursor-help' title={error}>
              {error}
            </div>
          </Tooltip>
        </div>
      )}

      {enableJsonPathSearch && (
        <Input
          classNames={{
            base: 'w-full',
            inputWrapper:
              'border-1 border-solid border-outline border-t-0 rounded-t-none'
          }}
          color='primary'
          variant='flat'
          placeholder={i18n.t('label.jsonpath_search_placeholder') as string}
          onChange={e => handleInputChange(e.target.value)}
          size='sm'
          endContent={<Search className='h-4 w-4' />}
        />
      )}

      {description && (
        <div className='mt-1 p-1 text-xs tracking-wide text-on-surface-variant'>
          {description}
        </div>
      )}
    </div>
  );
}
